import { useState, useEffect } from 'react';
import { MdAdd, MdClose } from "react-icons/md";
import { DateRangePicker, Range } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // Main style file
import 'react-date-range/dist/theme/default.css'; // Theme CSS file
import { FormData, SetFormData } from './FormData.tsx';

interface UnavailableDateRange {
    startDate: Date;
    endDate: Date;
  }

interface listingForm {
    formData: FormData;
    setFormData: SetFormData;
    setNextDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

const getFirstDayOfMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };
  

export default function WorkspaceAvailabilities({ formData, setFormData, setNextDisabled }: listingForm) {
    const [hasBookings, setHasBookings] = useState(false);
    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: getFirstDayOfMonth(),
            endDate: getFirstDayOfMonth(),
            key: 'selection'
        }
    ]);

    // Initialize local state with formData.unavailableDates
    const [unavailableDates, setUnavailableDates] = useState<UnavailableDateRange[]>(
        formData.unavailableDates || []
    );
    console.log(unavailableDates);

    // Sync local state with formData when it changes
    useEffect(() => {
        setUnavailableDates(formData.unavailableDates || []);
    }, [formData.unavailableDates]);

    const handleAddUnavailableDates = () => {
        // Ensure dates exist before proceeding
        if (!dateRange[0].startDate || !dateRange[0].endDate) {
            console.error('Date range is incomplete');
            return;
        }
    
        const newRange = {
            startDate: dateRange[0].startDate as Date, // Type assertion since we checked above
            endDate: dateRange[0].endDate as Date
        };
    
        const updatedDates = [
            ...(formData.unavailableDates || []),
            newRange
        ];
    
        setFormData({
            ...formData,
            unavailableDates: updatedDates
        });
    
        setDateRange([{
            startDate: getFirstDayOfMonth(),
            endDate: getFirstDayOfMonth(),
            key: 'selection'
        }]);
    };

    const handleRemoveDate = (index: number) => {
        const updatedDates = [...(formData.unavailableDates || [])];
        updatedDates.splice(index, 1);
        
        setFormData({
        ...formData,
        unavailableDates: updatedDates.length ? updatedDates : undefined
        });
    };
    useEffect(() => {
        if(formData.unavailableDates === undefined) return;
        if(hasBookings && formData.unavailableDates?.length === 0) {
            setNextDisabled(true);
        } else {
            setNextDisabled(false);
        }

        if(formData.unavailableDates?.length>0) {
            setHasBookings(true);
        }
    },[hasBookings, formData.unavailableDates])

  return (
    <div className="space-y-4">
      <h2 className="text-3xl font-bold mb-4">Current/Existing leases</h2>
      <hr className="border-t border-gray-500 my-4 w-full shadow-gray-400" />
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <input
            type="checkbox"
            id="hasBookings"
            checked={hasBookings}
            onChange={(e) => setHasBookings(e.target.checked)}
            className="h-5 w-5"
          />
          <label htmlFor="hasBookings" className="text-lg font-medium">
            Are there any existing bookings/leases?
          </label>
        </div>

        {hasBookings && (
          <div className="mt-4 space-y-4">
            <div className="border rounded-lg p-4">
              <DateRangePicker
                onChange={(item) => setDateRange([item.selection])}
                ranges={dateRange}
                months={2}
                direction="horizontal"
              />
            </div>

            <button
              onClick={handleAddUnavailableDates}
              className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              <MdAdd /> Add Unavailable Dates
            </button>

            {(formData.unavailableDates?.length ?? 0) > 0 && (
              <div className="mt-4">
                <h4 className="font-medium mb-2">Unavailable Dates:</h4>
                <ul className="space-y-2">
                  {formData.unavailableDates?.map((range, index) => (
                    <li key={index} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                      <span>
                        {range.startDate.toLocaleDateString() === range.endDate.toLocaleDateString()
                          ? range.startDate.toLocaleDateString()
                          : `${range.startDate.toLocaleDateString()} → ${range.endDate.toLocaleDateString()}`}
                      </span>
                      <button 
                        onClick={() => handleRemoveDate(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <MdClose size={18} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}